import { ReactFlowProvider } from '@xyflow/react';
import { lazy, Suspense, useEffect } from 'react';
import { useLocation } from 'react-router';

import { Drawer, Drawers } from '@/components/ui/Drawer';
import { AgentDrawer } from '@/sections/agents/components/drawer';
import { AssetDrawer } from '@/sections/asset/components/drawer';
import { CapabilityDrawer } from '@/sections/attacks/capabilities/CapabilityDrawer';
import { ParkedDomainDrawer } from '@/sections/attacks/redTeam/domain-parking/components/drawer';
import { CustomerDrawer } from '@/sections/customerDrawer';
import { IntegrationDrawer } from '@/sections/IntegrationDrawer';
import { PreseedDrawer } from '@/sections/preseed/PreseedDrawer';
import { SeedDrawerComponent } from '@/sections/seeds/components/SeedDrawer';
import { NotificationsDrawer } from '@/sections/settings/components';
import { TechnologyDrawer } from '@/sections/technology/components/drawer';
import { ThreatIntelDrawer } from '@/sections/threats/drawer';
import { useGlobalState } from '@/state/global.state';

// Lazy-load VulnerabilityDrawer to avoid pulling in heavy dependencies (jszip, xlsx, papaparse)
// These come from ImportVulnerabilitiesModal which is imported by VulnerabilityDrawer
const VulnerabilityDrawerComponent = lazy(() =>
  import('@/sections/vulnerabilities/components/VulnerabilityDrawer').then(
    module => ({ default: module.VulnerabilityDrawerComponent })
  )
);
import { IntegrationMeta } from '@/types';
import { StorageKey } from '@/utils/storage/useStorage.util';
import { useSearchParams } from '@/utils/url.util';

export function AppDrawers() {
  const { drawerState } = useGlobalState();
  const { searchParams } = useSearchParams();
  const location = useLocation();
  const isInsightsPage = location.pathname.includes('insights');

  // NOTE: Key-based pattern used - drawers fetch their own data internally
  const isIntegrationDrawer =
    searchParams.get(StorageKey.DRAWER_COMPOSITE_KEY) === 'integration';
  const integrationCategory =
    searchParams.get('integrationDrawerCategory') || '';

  // KEY-BASED PATTERN: Vulnerability query removed. VulnerabilityDrawerComponent now
  // fetches data internally via useVulnerabilityDrawerData(vulnerabilityDrawerKey).
  // This eliminates race conditions from dual state sources.

  // KEY-BASED PATTERN: Preseed queries removed. PreseedDrawer now
  // fetches data internally via usePreseedDrawerData(preseedDrawerKey).
  // This eliminates race conditions from dual state sources.

  // KEY-BASED PATTERN: Seed fetching is now handled by SeedDrawerComponent via useSeedDrawerData(seedDrawerKey).
  // The URL param (seedDrawerKey) is the single source of truth, eliminating race conditions.
  // Previously, this query would call setSeed() when API responded, which could overwrite
  // fresher synchronous state when clicking quickly between rows.

  // KEY-BASED PATTERN: Capability query removed. CapabilityDrawer now
  // fetches data internally via useCapabilityDrawerData(capabilityDrawerKey).
  // This eliminates race conditions from dual state sources.

  // KEY-BASED PATTERN: ParkedDomain query removed. ParkedDomainDrawer now
  // fetches data internally via useParkedDomainDrawerData(parkedDomainDrawerKey).
  // This eliminates race conditions from dual state sources.

  // KEY-BASED PATTERN: Vulnerability useEffect removed. VulnerabilityDrawerComponent now
  // fetches data internally via useVulnerabilityDrawerData(vulnerabilityDrawerKey).
  // This eliminates race conditions when clicking quickly between rows.

  // KEY-BASED PATTERN: Preseed useEffect removed. PreseedDrawer now
  // fetches data internally via usePreseedDrawerData(preseedDrawerKey).
  // This eliminates race conditions when clicking quickly between rows.

  useEffect(() => {
    if (isIntegrationDrawer && !drawerState.integration) {
      drawerState.setIntegration({} as IntegrationMeta);
    }
  }, [isIntegrationDrawer]);

  // KEY-BASED PATTERN: Seed state management is now handled by SeedDrawerComponent.
  // The useEffect that called setSeed(dataWithSeedSearch[0]) has been removed to eliminate
  // race conditions when clicking quickly between rows. The drawer component fetches
  // seed data internally via useSeedDrawerData(seedDrawerKey).

  // KEY-BASED PATTERN: Capability useEffect removed. CapabilityDrawer now
  // fetches data internally via useCapabilityDrawerData(capabilityDrawerKey).
  // This eliminates race conditions when clicking quickly between rows.

  // KEY-BASED PATTERN: ParkedDomain useEffect removed. ParkedDomainDrawer now
  // fetches data internally via useParkedDomainDrawerData(parkedDomainDrawerKey).
  // This eliminates race conditions when clicking quickly between rows.

  return (
    <Drawers disableBackdrop={isInsightsPage}>
      {drawerState.drawerOrder.map((drawerType, index) => {
        if (drawerType === 'agent' && drawerState.agentDrawerKey) {
          return (
            <Drawer
              key={`agent-${drawerState.agentDrawerKey}`}
              uuid={drawerState.agentDrawerKey}
              onClose={() => drawerState.handleOpenAgentDrawer()}
              open
              escapeKey
            >
              <AgentDrawer />
            </Drawer>
          );
        } else if (
          drawerType === 'technology' &&
          drawerState.technologyDrawerKey
        ) {
          // STABLE KEY FIX 2024-12-09: Use key={index} instead of key={`technology-${key}`}
          // to prevent component remount on row navigation (which loses tab state)
          return (
            <Drawer
              key={index}
              uuid={drawerState.technologyDrawerKey}
              onClose={() => drawerState.handleOpenTechnologyDrawer()}
              open
            >
              <TechnologyDrawer drawerKey={drawerState.technologyDrawerKey} />
            </Drawer>
          );
        } else if (drawerType === 'asset' && drawerState.assetDrawerKey) {
          return (
            <Drawer
              key={index}
              uuid={drawerState.assetDrawerKey}
              onClose={() => drawerState.handleOpenAssetDrawer()}
              open
            >
              <ReactFlowProvider>
                <AssetDrawer />
              </ReactFlowProvider>
            </Drawer>
          );
        } else if (
          drawerType === 'vulnerability' &&
          drawerState.vulnerabilityDrawerKey
        ) {
          // KEY-BASED PATTERN: Drawer renders based on key, not object state.
          // VulnerabilityDrawerComponent fetches its own data via useVulnerabilityDrawerData(key).
          return (
            <Drawer
              key={index}
              uuid={drawerState.vulnerabilityDrawerKey}
              scrollResetKey={drawerState.vulnerabilityDrawerTab}
              onClose={() => drawerState.handleOpenVulnerabilityDrawer()}
              open
            >
              <Suspense fallback={null}>
                <VulnerabilityDrawerComponent />
              </Suspense>
            </Drawer>
          );
        } else if (drawerType === 'seed' && drawerState.seedDrawerKey) {
          return (
            <Drawer
              key={index}
              uuid={drawerState.seedDrawerKey}
              onClose={() => drawerState.handleOpenSeedDrawer()}
              open
            >
              <SeedDrawerComponent />
            </Drawer>
          );
        } else if (drawerType === 'integration' && drawerState.integration) {
          return (
            <Drawer
              key={index}
              uuid={drawerState.integrationDrawerKey}
              open={Boolean(drawerState.integration)}
              onClose={() => drawerState.handleOpenIntegrationDrawer()}
              className="rounded-t-lg pb-0 shadow-lg"
              initialWidth="77%"
            >
              <IntegrationDrawer initialCategory={integrationCategory} />
            </Drawer>
          );
        } else if (
          drawerType === 'threatIntel' &&
          drawerState.threatIntelDrawerKey
        ) {
          // KEY-BASED PATTERN: Drawer renders based on key, not object state.
          // ThreatIntelDrawer fetches its own data via useThreatIntelDrawerData(key).
          return (
            <Drawer
              key={index}
              uuid={drawerState.threatIntelDrawerKey}
              open={Boolean(drawerState.threatIntelDrawerKey)}
              onClose={() => drawerState.handleOpenThreatIntelDrawer()}
            >
              <ThreatIntelDrawer />
            </Drawer>
          );
        } else if (drawerType === 'preseed' && drawerState.preseedDrawerKey) {
          // KEY-BASED PATTERN: Drawer renders based on key, not object state.
          // PreseedDrawer fetches its own data via usePreseedDrawerData(key).
          return (
            <Drawer
              key={index}
              uuid={drawerState.preseedDrawerKey}
              open={Boolean(drawerState.preseedDrawerKey)}
              onClose={() => drawerState.handleOpenPreseedDrawer()}
            >
              <ReactFlowProvider>
                <PreseedDrawer />
              </ReactFlowProvider>
            </Drawer>
          );
        } else if (
          drawerType === 'capability' &&
          drawerState.capabilityDrawerKey
        ) {
          // KEY-BASED PATTERN: Use capabilityDrawerKey as key to force remount when capability changes.
          // This resets all useState hooks in CapabilityDrawer automatically, eliminating
          // the need for useEffect with setState calls.
          return (
            <Drawer
              key={drawerState.capabilityDrawerKey}
              uuid={drawerState.capabilityDrawerKey}
              open={Boolean(drawerState.capabilityDrawerKey)}
              onClose={() => drawerState.handleOpenCapabilityDrawer()}
            >
              <CapabilityDrawer />
            </Drawer>
          );
        } else if (drawerType === 'customer' && drawerState.customer) {
          return (
            <Drawer
              key={index}
              uuid={drawerState.customerDrawerKey}
              open={Boolean(drawerState.customer)}
              onClose={() => drawerState.handleOpenCustomerDrawer()}
            >
              <CustomerDrawer customer={drawerState.customer} />
            </Drawer>
          );
        } else if (
          drawerType === 'parkedDomain' &&
          drawerState.parkedDomainDrawerKey
        ) {
          // KEY-BASED PATTERN: Drawer renders based on key, not object state.
          // ParkedDomainDrawer fetches its own data via useParkedDomainDrawerData(key).
          return (
            <Drawer
              key={index}
              uuid={drawerState.parkedDomainDrawerKey}
              open={Boolean(drawerState.parkedDomainDrawerKey)}
              onClose={() => drawerState.handleOpenParkedDomainDrawer()}
            >
              <ParkedDomainDrawer />
            </Drawer>
          );
        }
      })}
      {Boolean(drawerState.notificationsDrawerOpen) && (
        <Drawer
          key={'notifications'}
          uuid={'notifications'}
          open
          onClose={() => {
            drawerState.setNotificationsDrawerOpen(false);
          }}
        >
          <NotificationsDrawer />
        </Drawer>
      )}
    </Drawers>
  );
}
